import React from 'react'
import moment from 'moment'
import { Link as RouterLink } from 'react-router-dom'
import EmployeeSelector from '../../../events/EmployeeSelector'
import ShiftBonusEditor from '../../../../components/Forms/ShiftBonusEditor'
import ShiftTravelEditor from '../../../../components/Forms/ShiftTravelEditor'

import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteIcon from '@mui/icons-material/Delete'
import EventIcon from '@mui/icons-material/Event'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk'
import SaveIcon from '@mui/icons-material/Save'
import ViewWeekIcon from '@mui/icons-material/ViewWeek'

import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers'

import {
  getEmployeeName,
  getEntityId,
  getRosterLabel,
  getShiftSource,
  ON_CALL_TEAMS,
} from './globalRosterUtils'

const repeatDayOptions = [
  { label: 'M', tooltip: 'Monday', value: 1 },
  { label: 'T', tooltip: 'Tuesday', value: 2 },
  { label: 'W', tooltip: 'Wednesday', value: 3 },
  { label: 'T', tooltip: 'Thursday', value: 4 },
  { label: 'F', tooltip: 'Friday', value: 5 },
  { label: 'S', tooltip: 'Saturday', value: 6 },
  { label: 'S', tooltip: 'Sunday', value: 0 },
]

const getRepeatShiftCount = (shiftForm) => {
  if (!shiftForm?.repeat?.enabled) return 1
  if (
    !shiftForm.start ||
    !shiftForm.repeat?.until ||
    !shiftForm.repeat?.daysOfWeek?.length
  ) {
    return 0
  }

  const selectedDays = new Set(shiftForm.repeat.daysOfWeek)
  const cursor = moment(shiftForm.start).clone().startOf('day')
  const until = moment(shiftForm.repeat.until).clone().endOf('day')
  let count = 0

  while (cursor.isSameOrBefore(until, 'day')) {
    if (selectedDays.has(cursor.day())) count += 1
    cursor.add(1, 'day')
  }

  return count
}

const getAssignedLabel = (shiftForm) => {
  if (shiftForm?.assignedToSubcontractor) return 'subcontractor cover'
  const userAccount = shiftForm?.assignedTo?.userAccount
  if (userAccount) {
    return (
      `${userAccount.firstName ?? ''} ${userAccount.lastName ?? ''}`.trim() ||
      'selected staff member'
    )
  }
  if (shiftForm?.assignedTo?.subcontractor) {
    return (
      `${shiftForm.assignedTo.firstName ?? ''} ${shiftForm.assignedTo.lastName ?? ''}`.trim() ||
      'selected contractor'
    )
  }
  if (shiftForm?.assignedTo) {
    return (
      `${shiftForm.assignedTo.firstName ?? ''} ${shiftForm.assignedTo.lastName ?? ''}`.trim() ||
      'selected staff member'
    )
  }
  return ''
}

const getSourceLinks = (shift) => {
  const eventID = getEntityId(shift?.event)
  const ptsDutyID = getEntityId(shift?.ptsDuty)

  return [
    eventID && {
      label: 'Open Event',
      url: `/events/${eventID}`,
      icon: <EventIcon fontSize="small" />,
    },
    ptsDutyID && {
      label: 'Open PTS Duty',
      url: `/pts/planning?date=${moment(shift?.start).format(
        'YYYYMMDD',
      )}&duty=${ptsDutyID}`,
      icon: <ViewWeekIcon fontSize="small" />,
    },
  ].filter(Boolean)
}

const ShiftApplicationsPanel = ({ applications, onReview }) => {
  if (!applications.length) return null

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
      <Stack gap={1}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="subtitle2" fontWeight={800}>
            Applications for this shift
          </Typography>
          <Chip
            size="small"
            color="warning"
            label={`${applications.length} pending`}
            sx={{ borderRadius: 1 }}
          />
        </Stack>
        {applications.map((application) => (
          <Stack
            key={application._id}
            direction="row"
            gap={1}
            alignItems="center"
            justifyContent="space-between"
            sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: 'grey.50',
            }}
          >
            <Stack direction="row" gap={1} alignItems="center" minWidth={0}>
              <Avatar
                src={application.employee?.userAccount?.photograph}
                sx={{ width: 30, height: 30 }}
              />
              <Stack minWidth={0}>
                <Typography variant="body2" fontWeight={700} noWrap>
                  {getEmployeeName(application.employee)}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  Applied {moment(application.createdAt).format('D MMM HH:mm')}
                </Typography>
              </Stack>
            </Stack>
            <Button
              size="small"
              variant="outlined"
              onClick={() => onReview(application)}
            >
              Review
            </Button>
          </Stack>
        ))}
      </Stack>
    </Paper>
  )
}

// Someone said "Cannot Attend" but is still on the shift (decline keeps the
// assignee until cover is sorted). Accepting releases them and leaves the
// shift open on Available Shifts; colleagues who already applied for the
// swap show in the applications panel above, ready to approve.
const CannotAttendPanel = ({
  shift,
  locked,
  applicationsCount,
  releasing,
  onRelease,
}) => {
  const declinedAt = shift?.acknowledgement?.declinedAt
  if (!declinedAt || !shift?.assignedTo) return null

  const started = moment(shift.start).isSameOrBefore(moment())
  const canRelease = !locked && !started

  return (
    <Alert
      severity="error"
      icon={<EventBusyIcon />}
      sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
    >
      <Stack gap={1}>
        <Typography variant="body2" fontWeight={700}>
          {getEmployeeName(shift.assignedTo)} can't attend
        </Typography>
        <Typography variant="body2">
          Marked {moment(declinedAt).format('D MMM YYYY HH:mm')}
          {shift.acknowledgement?.declineReason
            ? ` - "${shift.acknowledgement.declineReason}"`
            : ''}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {applicationsCount > 0
            ? `${applicationsCount} colleague${applicationsCount === 1 ? ' has' : 's have'} applied to cover it - approve one above to swap them in, or release the shift to open it up.`
            : 'Releasing removes them from the shift and lists it on Available Shifts so other staff can book it.'}
        </Typography>
        {canRelease && (
          <Box>
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={onRelease}
              disabled={releasing}
            >
              {releasing ? 'Releasing...' : 'Accept & release shift'}
            </Button>
          </Box>
        )}
        {started && (
          <Typography variant="caption">
            This shift has already started, so it can't be released.
          </Typography>
        )}
      </Stack>
    </Alert>
  )
}

// Clearing the assignee on a shift that isn't bookable leaves it vacant but
// invisible to staff - say so next to the switch that fixes it
const NotAdvertisedHint = ({ shiftForm, show = true }) => {
  if (!show || !shiftForm || shiftForm.staffCanBook) return null
  if (shiftForm.assignedTo || shiftForm.assignedToSubcontractor) return null
  return (
    <Typography variant="caption" color="warning.main" sx={{ mt: -1.5 }}>
      Not advertised to staff - turn on "Staff can book" to list this vacancy
      on Available Shifts.
    </Typography>
  )
}

const ShiftDrawer = ({
  open,
  onClose,
  selectedShift,
  selectedShiftIsRosterShift,
  selectedShiftLocked,
  selectedShiftApplications,
  setSelectedApplication,
  shiftForm,
  setShiftForm,
  shiftIsOnCall,
  rosters,
  selectedRoster,
  copyShiftToSelectedRoster,
  handleRoleChange,
  checkingAssignment,
  assignmentIssues,
  orgSettings,
  hasBlockingAssignmentIssues,
  shiftSaving,
  deleteShift,
  saveShift,
  saveSourceShiftEdit,
  releaseShift,
  releasingShift,
}) => {
  const repeatCount = getRepeatShiftCount(shiftForm)
  const assignedLabel = getAssignedLabel(shiftForm)
  const duplicateCount = selectedShift?.duplicateCount ?? 1
  const isBulkEdit = duplicateCount > 1
  // Once a plan is published its shifts are live - the draft-plan picker
  // (which only ever lists Draft plans) would render blank and only offer
  // wrong options, so show the origin plan read-only instead.
  const shiftRosterPublished =
    !!selectedShift?.roster &&
    typeof selectedShift.roster === 'object' &&
    selectedShift.roster.status !== 'Draft'

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 460 },
          height: '100dvh',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          p: 2,
        },
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexShrink={0}
      >
        <Stack>
          <Typography variant="h6">
            {selectedShift
              ? 'Shift Details'
              : shiftIsOnCall
                ? 'New On-Call Shift'
                : 'New Rota Plan Shift'}
          </Typography>
          {selectedShift && (
            <Typography variant="caption">
              {getShiftSource(selectedShift)}
            </Typography>
          )}
        </Stack>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>

      <Divider sx={{ my: 2, flexShrink: 0 }} />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5, pb: 2 }}>
        {selectedShift && selectedRoster && (
          <Button
            startIcon={<ContentCopyIcon />}
            variant="contained"
            color="contrast"
            onClick={copyShiftToSelectedRoster}
            sx={{ mb: 2, justifyContent: 'flex-start' }}
            fullWidth
          >
            Copy to current rota plan: {getRosterLabel(selectedRoster)}
          </Button>
        )}

        <ShiftApplicationsPanel
          applications={selectedShiftApplications}
          onReview={setSelectedApplication}
        />

        <CannotAttendPanel
          shift={selectedShift}
          locked={selectedShiftLocked}
          applicationsCount={selectedShiftApplications.length}
          releasing={releasingShift}
          onRelease={releaseShift}
        />

        {!selectedShiftIsRosterShift ? (
          <Stack gap={2}>
          <Alert severity={isBulkEdit ? 'warning' : 'info'}>
            <Stack gap={1}>
              <Typography variant="body2">
                {isBulkEdit
                  ? `There are ${duplicateCount} identical vacant shifts here (same event, role, time and pay). Saving changes below updates all ${duplicateCount} at once. To assign one of them to a specific person, or edit just one, open the source module below and edit it there.`
                  : `${getShiftSource(selectedShift)} shift - edit times, role, pay and assignment here, or open the source module below.`}
              </Typography>
              {getSourceLinks(selectedShift).length > 0 && (
                <Stack direction="row" gap={1} flexWrap="wrap">
                  {getSourceLinks(selectedShift).map((link) => (
                    <Button
                      key={link.url}
                      component={RouterLink}
                      to={link.url}
                      size="small"
                      variant="outlined"
                      startIcon={link.icon}
                      onClick={onClose}
                    >
                      {link.label}
                    </Button>
                  ))}
                </Stack>
              )}
            </Stack>
          </Alert>
          {shiftForm && (
            <Stack gap={2}>
              {selectedShiftLocked && (
                <Alert severity="warning">
                  This shift is verified or paid and cannot be edited.
                </Alert>
              )}
              <DateTimePicker
                label="Start"
                value={shiftForm.start}
                onChange={(value) =>
                  setShiftForm((prev) => ({ ...prev, start: value }))
                }
                disabled={selectedShiftLocked}
              />
              <DateTimePicker
                label="End"
                value={shiftForm.end}
                onChange={(value) =>
                  setShiftForm((prev) => ({ ...prev, end: value }))
                }
                disabled={selectedShiftLocked}
              />
              <TextField
                select
                label="Role"
                value={shiftForm.clinicalRole}
                onChange={(e) => handleRoleChange(e.target.value)}
                disabled={selectedShiftLocked}
                fullWidth
              >
                {(orgSettings?.roles ?? []).map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </TextField>
              <EmployeeSelector
                label="Assigned staff"
                value={shiftForm.assignedTo}
                onChange={(e, value) =>
                  setShiftForm((prev) => ({
                    ...prev,
                    assignedTo: value,
                    assignedSubcontractor: null,
                    assignedToSubcontractor: false,
                    clinicalRole:
                      value?.subcontractor?.clinicalRole ||
                      value?.employee?.clinicalRole ||
                      value?.clinicalRole ||
                      prev.clinicalRole,
                  }))
                }
                fullWidth
                includeSubcontractor
                disabled={
                  selectedShiftLocked ||
                  isBulkEdit ||
                  !!shiftForm.assignedToSubcontractor
                }
                helperText={
                  isBulkEdit
                    ? 'Assign one of these shifts from the source module'
                    : undefined
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!shiftForm.assignedToSubcontractor}
                    onChange={(e) =>
                      setShiftForm((prev) => ({
                        ...prev,
                        assignedToSubcontractor: e.target.checked,
                        assignedTo: e.target.checked ? null : prev.assignedTo,
                        assignedSubcontractor: null,
                      }))
                    }
                    disabled={selectedShiftLocked || isBulkEdit}
                  />
                }
                label="Covered by subcontractor without a user account"
              />
              {checkingAssignment && <LinearProgress />}
              {assignmentIssues.length > 0 && (
                <Stack gap={1}>
                  {assignmentIssues.map((assignmentIssue) => (
                    <Alert
                      key={`${assignmentIssue.code}-${assignmentIssue.message}`}
                      severity={
                        assignmentIssue.severity === 'warning'
                          ? 'warning'
                          : 'error'
                      }
                    >
                      {assignmentIssue.message}
                    </Alert>
                  ))}
                </Stack>
              )}
              <Stack direction="row" gap={2}>
                <TextField
                  label="Pay rate"
                  value={shiftForm.payRate}
                  onChange={(e) =>
                    setShiftForm((prev) => ({
                      ...prev,
                      payRate: e.target.value,
                    }))
                  }
                  disabled={selectedShiftLocked}
                  type="number"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {orgSettings?.currency ?? '£'}
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!shiftForm.hourlyRate}
                      onChange={(e) =>
                        setShiftForm((prev) => ({
                          ...prev,
                          hourlyRate: e.target.checked,
                        }))
                      }
                      disabled={selectedShiftLocked}
                    />
                  }
                  label="Hourly"
                />
              </Stack>
              <ShiftBonusEditor
                bonuses={shiftForm.bonuses ?? []}
                onChange={(bonuses) =>
                  setShiftForm((prev) => ({ ...prev, bonuses }))
                }
                disabled={selectedShiftLocked}
                currency={orgSettings?.currency ?? '£'}
              />
              <ShiftTravelEditor
                travel={shiftForm.travel ?? null}
                onChange={(travel) =>
                  setShiftForm((prev) => ({ ...prev, travel }))
                }
                disabled={selectedShiftLocked}
                currency={orgSettings?.currency ?? '£'}
                defaultRate={orgSettings?.defaultTravelRate}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!shiftForm.staffCanBook}
                    onChange={(e) =>
                      setShiftForm((prev) => ({
                        ...prev,
                        staffCanBook: e.target.checked,
                      }))
                    }
                    disabled={selectedShiftLocked}
                  />
                }
                label="Staff can book"
              />
              <NotAdvertisedHint shiftForm={shiftForm} show={!isBulkEdit} />
              <TextField
                label="Notes"
                value={shiftForm.shiftNote}
                onChange={(e) =>
                  setShiftForm((prev) => ({
                    ...prev,
                    shiftNote: e.target.value,
                  }))
                }
                disabled={selectedShiftLocked}
                multiline
                minRows={3}
                fullWidth
              />
              <Button
                startIcon={<SaveIcon />}
                variant="contained"
                disabled={
                  shiftSaving ||
                  selectedShiftLocked ||
                  hasBlockingAssignmentIssues ||
                  !shiftForm.start ||
                  !shiftForm.end ||
                  !shiftForm.clinicalRole
                }
                onClick={saveSourceShiftEdit}
              >
                {isBulkEdit ? `Save All ${duplicateCount} Shifts` : 'Save Shift'}
              </Button>
            </Stack>
          )}
          </Stack>
        ) : (
          shiftForm && (
            <Stack gap={2}>
            {selectedShiftLocked && (
              <Alert severity="warning">
                This shift is verified or paid and cannot be edited.
              </Alert>
            )}
            {shiftIsOnCall && (
              <Alert
                severity="info"
                icon={<PhoneInTalkIcon fontSize="inherit" />}
              >
                On-call shift: only staff in the {ON_CALL_TEAMS.join(' or ')}{' '}
                teams can be assigned. Overlapping shifts and approved leave are
                shown as warnings and won&apos;t block assignment.
              </Alert>
            )}
            {shiftRosterPublished ? (
              <TextField
                label="Published from"
                value={getRosterLabel(selectedShift.roster)}
                fullWidth
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Chip
                        size="small"
                        color={
                          selectedShift.roster.status === 'Published'
                            ? 'success'
                            : 'default'
                        }
                        label={selectedShift.roster.status}
                        sx={{ borderRadius: 1, fontWeight: 700 }}
                      />
                    </InputAdornment>
                  ),
                }}
                helperText="This shift is live on the roster - its rota plan has been published, so it no longer appears in draft planning. Edits here update the live shift directly."
              />
            ) : (
            <Autocomplete
              options={rosters}
              value={rosters.find((r) => r._id === shiftForm.roster) ?? null}
              onChange={(e, value) =>
                setShiftForm((prev) => ({
                  ...prev,
                  roster: value?._id ?? '',
                  // Subcontractors and self-booking are not allowed on on-call rotas
                  ...(value?.isOnCall
                    ? {
                        assignedSubcontractor: null,
                        assignToSubcontractor: false,
                        assignedToSubcontractor: false,
                        staffCanBook: false,
                        ...(prev.assignedTo?.isSubcontractor ||
                        prev.assignedTo?.subcontractor
                          ? { assignedTo: null }
                          : {}),
                      }
                    : {}),
                }))
              }
              getOptionLabel={getRosterLabel}
              isOptionEqualToValue={(a, b) => a?._id === b?._id}
              renderInput={(params) => (
                <TextField {...params} label="Rota plan" required />
              )}
              disabled={selectedShiftLocked}
            />
            )}
            <DateTimePicker
              label="Start"
              value={shiftForm.start}
              onChange={(value) =>
                setShiftForm((prev) => ({ ...prev, start: value }))
              }
              disabled={selectedShiftLocked}
            />
            <DateTimePicker
              label="End"
              value={shiftForm.end}
              onChange={(value) =>
                setShiftForm((prev) => ({ ...prev, end: value }))
              }
              disabled={selectedShiftLocked}
            />
            {!selectedShift && (
              <Stack gap={1}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!shiftForm.repeat?.enabled}
                      onChange={(e) =>
                        setShiftForm((prev) => ({
                          ...prev,
                          repeat: {
                            ...(prev.repeat || {}),
                            enabled: e.target.checked,
                            daysOfWeek: e.target.checked
                              ? prev.repeat?.daysOfWeek?.length
                                ? prev.repeat.daysOfWeek
                                : [moment(prev.start).day()]
                              : [],
                            until: e.target.checked
                              ? prev.repeat?.until || moment(prev.start)
                              : null,
                            assignAssigneeToAll: false,
                          },
                        }))
                      }
                    />
                  }
                  label="Repeat this shift"
                />
                {!!shiftForm.repeat?.enabled && (
                  <Stack gap={1}>
                    <Stack direction="row" gap={0.75} flexWrap="wrap">
                      {repeatDayOptions.map((day) => {
                        const checked = shiftForm.repeat.daysOfWeek?.includes(
                          day.value,
                        )

                        return (
                          <Tooltip key={day.value} title={day.tooltip}>
                            <Button
                              size="small"
                              variant={checked ? 'contained' : 'outlined'}
                              onClick={() =>
                                setShiftForm((prev) => {
                                  const days = new Set(
                                    prev.repeat?.daysOfWeek || [],
                                  )
                                  if (days.has(day.value)) {
                                    days.delete(day.value)
                                  } else {
                                    days.add(day.value)
                                  }

                                  return {
                                    ...prev,
                                    repeat: {
                                      ...(prev.repeat || {}),
                                      daysOfWeek: [...days].sort(),
                                    },
                                  }
                                })
                              }
                              sx={{
                                minWidth: 34,
                                width: 34,
                                height: 34,
                                px: 0,
                                borderRadius: '50%',
                              }}
                            >
                              {day.label}
                            </Button>
                          </Tooltip>
                        )
                      })}
                    </Stack>
                    <DatePicker
                      label="Repeat until"
                      value={shiftForm.repeat.until}
                      onChange={(value) =>
                        setShiftForm((prev) => ({
                          ...prev,
                          repeat: {
                            ...(prev.repeat || {}),
                            until: value,
                          },
                        }))
                      }
                    />
                    <Alert severity={repeatCount > 0 ? 'info' : 'warning'}>
                      {repeatCount > 0
                        ? `${repeatCount} shift${repeatCount === 1 ? '' : 's'} will be created${
                            assignedLabel &&
                            repeatCount > 1 &&
                            shiftForm.repeat?.assignAssigneeToAll === false
                              ? '; only the first will be assigned.'
                              : '.'
                          }`
                        : 'Choose at least one day and an end date.'}
                    </Alert>
                  </Stack>
                )}
              </Stack>
            )}
            {!shiftIsOnCall && (
              <TextField
                select
                label="Role"
                value={shiftForm.clinicalRole}
                onChange={(e) => handleRoleChange(e.target.value)}
                disabled={selectedShiftLocked}
                fullWidth
              >
                {(orgSettings?.roles ?? []).map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <EmployeeSelector
              label={shiftIsOnCall ? 'On-call staff' : 'Assigned staff'}
              value={shiftForm.assignedTo}
              onChange={(e, value) =>
                setShiftForm((prev) => ({
                  ...prev,
                  assignedTo: value,
                  assignedSubcontractor: null,
                  assignedToSubcontractor: false,
                  clinicalRole:
                    value?.subcontractor?.clinicalRole ||
                    value?.employee?.clinicalRole ||
                    value?.clinicalRole ||
                    prev.clinicalRole,
                }))
              }
              fullWidth
              team={shiftIsOnCall ? ON_CALL_TEAMS : undefined}
              includeSubcontractor={!shiftIsOnCall}
              helperText={
                shiftIsOnCall
                  ? `${ON_CALL_TEAMS.join(' and ')} team members only`
                  : undefined
              }
              disabled={
                selectedShiftLocked || !!shiftForm.assignedToSubcontractor
              }
            />
            {!shiftIsOnCall && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!shiftForm.assignedToSubcontractor}
                    onChange={(e) =>
                      setShiftForm((prev) => ({
                        ...prev,
                        assignedToSubcontractor: e.target.checked,
                        assignedTo: e.target.checked ? null : prev.assignedTo,
                        assignedSubcontractor: null,
                      }))
                    }
                    disabled={selectedShiftLocked}
                  />
                }
                label="Covered by subcontractor without a user account"
              />
            )}
            {!selectedShift &&
              shiftForm.repeat?.enabled &&
              repeatCount > 1 && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderColor: '#239AA4',
                    bgcolor: 'rgba(35, 154, 164, 0.08)',
                    borderRadius: 1,
                  }}
                >
                  <Stack gap={0.75}>
                    <Typography variant="subtitle2" fontWeight={800}>
                      Repeat assignment
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!shiftForm.repeat?.assignAssigneeToAll}
                          disabled={!assignedLabel}
                          onChange={(e) =>
                            setShiftForm((prev) => ({
                              ...prev,
                              repeat: {
                                ...(prev.repeat || {}),
                                assignAssigneeToAll: e.target.checked,
                              },
                            }))
                          }
                        />
                      }
                      label={
                        assignedLabel
                          ? `Assign ${assignedLabel} to all ${repeatCount} shifts`
                          : `Assign selected person to all ${repeatCount} shifts`
                      }
                    />
                    <Typography variant="caption" color="text.secondary">
                      {assignedLabel
                        ? 'Leave this off to assign only the first shift.'
                        : 'Select a staff member or contractor cover to use this option.'}
                    </Typography>
                  </Stack>
                </Paper>
              )}
            {checkingAssignment && <LinearProgress />}
            {assignmentIssues.length > 0 && (
              <Stack gap={1}>
                {assignmentIssues.map((assignmentIssue) => (
                  <Alert
                    key={`${assignmentIssue.code}-${assignmentIssue.message}`}
                    severity={
                      assignmentIssue.severity === 'warning'
                        ? 'warning'
                        : 'error'
                    }
                  >
                    {assignmentIssue.message}
                  </Alert>
                ))}
              </Stack>
            )}
            <Stack direction="row" gap={2}>
              <TextField
                label="Pay rate"
                value={shiftForm.payRate}
                onChange={(e) =>
                  setShiftForm((prev) => ({
                    ...prev,
                    payRate: e.target.value,
                  }))
                }
                disabled={selectedShiftLocked}
                type="number"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {orgSettings?.currency ?? '£'}
                    </InputAdornment>
                  ),
                }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!shiftForm.hourlyRate}
                    onChange={(e) =>
                      setShiftForm((prev) => ({
                        ...prev,
                        hourlyRate: e.target.checked,
                      }))
                    }
                    disabled={selectedShiftLocked}
                  />
                }
                label="Hourly"
              />
            </Stack>
            <ShiftBonusEditor
              bonuses={shiftForm.bonuses ?? []}
              onChange={(bonuses) =>
                setShiftForm((prev) => ({ ...prev, bonuses }))
              }
              disabled={selectedShiftLocked}
              currency={orgSettings?.currency ?? '£'}
            />
            <ShiftTravelEditor
              travel={shiftForm.travel ?? null}
              onChange={(travel) =>
                setShiftForm((prev) => ({ ...prev, travel }))
              }
              disabled={selectedShiftLocked}
              currency={orgSettings?.currency ?? '£'}
              defaultRate={orgSettings?.defaultTravelRate}
            />
            {!shiftIsOnCall && (
              <FormControlLabel
                control={
                  <Switch
                    checked={!!shiftForm.staffCanBook}
                    onChange={(e) =>
                      setShiftForm((prev) => ({
                        ...prev,
                        staffCanBook: e.target.checked,
                      }))
                    }
                    disabled={selectedShiftLocked}
                  />
                }
                label="Staff can book"
              />
            )}
            <NotAdvertisedHint
              shiftForm={shiftForm}
              show={!shiftIsOnCall && !!selectedShift}
            />
            <TextField
              label="Notes"
              value={shiftForm.shiftNote}
              onChange={(e) =>
                setShiftForm((prev) => ({
                  ...prev,
                  shiftNote: e.target.value,
                }))
              }
              disabled={selectedShiftLocked}
              multiline
              minRows={3}
              fullWidth
            />
            <Stack direction="row" justifyContent="space-between" gap={1}>
              <Button
                startIcon={<DeleteIcon />}
                color="error"
                disabled={!selectedShift || selectedShiftLocked}
                onClick={deleteShift}
              >
                Delete
              </Button>
              <Button
                startIcon={<SaveIcon />}
                variant="contained"
                disabled={
                  shiftSaving ||
                  selectedShiftLocked ||
                  hasBlockingAssignmentIssues ||
                  !shiftForm.roster ||
                  !shiftForm.start ||
                  !shiftForm.end ||
                  (!shiftForm.clinicalRole && !shiftIsOnCall) ||
                  (!selectedShift &&
                    shiftForm.repeat?.enabled &&
                    repeatCount < 1)
                }
                onClick={saveShift}
              >
                Save Shift
              </Button>
            </Stack>
            </Stack>
          )
        )}
      </Box>
    </Drawer>
  )
}

export default ShiftDrawer
